import { type FC } from 'react';
import { FileText, Receipt, DollarSign, Banknote } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useAuth } from '@/auth';
import { checkAllPermissions, PERMISSIONS } from '@/auth/permissions';

export interface TramiteBudgetIndicatorsProps {
  tramite: any;
}

/**
 * Helper function to check if a currency is fully paid
 */
const isCurrencyFullyPaid = (
  presupuesto: any,
  currency: 'ARS' | 'USD'
): { isPaid: boolean; totalPaid: number; totalAmount: number } => {
  if (!presupuesto) {
    return { isPaid: false, totalPaid: 0, totalAmount: 0 };
  }

  const totalAmountKey = currency === 'ARS' ? 'ars_monto_total' : 'usd_monto_total';
  const totalAmount = parseFloat(presupuesto[totalAmountKey] || 0);

  if (!totalAmount || !presupuesto.cobros || !Array.isArray(presupuesto.cobros)) {
    return { isPaid: false, totalPaid: 0, totalAmount };
  }

  const totalPaid = presupuesto.cobros
    .filter((cobro: any) => cobro.moneda === currency)
    .reduce((sum: number, cobro: any) => sum + parseFloat(cobro.monto || 0), 0);

  // Consider paid if totalPaid >= totalAmount (with small margin for rounding)
  const isPaid = totalPaid >= totalAmount - 0.01;

  return { isPaid, totalPaid, totalAmount };
};

const formatCurrency = (amount: number, currency: 'ARS' | 'USD') => {
  return new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

const TramiteBudgetIndicators: FC<TramiteBudgetIndicatorsProps> = ({
  tramite,
}) => {
  const { user } = useAuth();
  const userPermissions = user?.permissions || [];

  // Check permissions for viewing presupuestos data
  const canViewPresupuestosFiles = checkAllPermissions(userPermissions, [PERMISSIONS.VIEW_PRESUPUESTOS_FILES.name]);
  const canViewPresupuestos = checkAllPermissions(userPermissions, [PERMISSIONS.VIEW_PRESUPUESTOS.name]);
  const canViewCostos = checkAllPermissions(userPermissions, [PERMISSIONS.VIEW_COSTOS.name]);

  // Presupuesto independiente importado en la escritura (lo calcula el listado).
  const tienePresupuestoVinculado = Boolean(tramite.tiene_presupuesto_vinculado);
  const factura = tramite.presupuesto?.documentos?.find((doc: any) => doc.tipo === 'factura');

  // Check payment status for each currency
  const arsPayment = isCurrencyFullyPaid(tramite.presupuesto, 'ARS');
  const usdPayment = isCurrencyFullyPaid(tramite.presupuesto, 'USD');

  // Only show currency indicators if the presupuesto is initiated for that currency
  const showARS = tramite.presupuesto?.ars_iniciado;
  const showUSD = tramite.presupuesto?.usd_iniciado;

  return (
    <div className="flex items-center gap-1.5">
      {/* Presupuesto vinculado - Requires VIEW_COSTOS (presupuesto-items.view) permission */}
      {canViewCostos && (
        <Tooltip>
          <TooltipTrigger asChild>
            <FileText
              aria-label={tienePresupuestoVinculado ? 'Presupuesto asociado' : 'Sin presupuesto asociado'}
              className={`h-4 w-4 ${tienePresupuestoVinculado ? 'text-blue-600' : 'text-gray-300'}`}
            />
          </TooltipTrigger>
          <TooltipContent>
            {tienePresupuestoVinculado ? 'Presupuesto asociado' : 'Sin presupuesto asociado'}
          </TooltipContent>
        </Tooltip>
      )}

      {/* Factura - Requires VIEW_PRESUPUESTOS_FILES permission */}
      {canViewPresupuestosFiles && (
        <Tooltip>
          <TooltipTrigger asChild>
            <Receipt
              className={`h-4 w-4 ${factura ? 'text-green-600' : 'text-gray-300'}`}
            />
          </TooltipTrigger>
          <TooltipContent>
            {factura ? 'Factura subida' : 'Factura no subida'}
          </TooltipContent>
        </Tooltip>
      )}

      {/* ARS Payment Status - Requires VIEW_PRESUPUESTOS permission */}
      {canViewPresupuestos && showARS && (
        <Tooltip>
          <TooltipTrigger asChild>
            <Banknote
              className={`h-4 w-4 ${arsPayment.isPaid ? 'text-green-600' : 'text-orange-500'}`}
            />
          </TooltipTrigger>
          <TooltipContent>
            <div className="text-xs">
              <div className="font-semibold">
                {arsPayment.isPaid ? 'ARS: Cobrado completamente' : 'ARS: Cobro pendiente'}
              </div>
              <div className='font-bold'>
                {formatCurrency(arsPayment.totalPaid, 'ARS')} / {formatCurrency(arsPayment.totalAmount, 'ARS')}
              </div>
            </div>
          </TooltipContent>
        </Tooltip>
      )}

      {/* USD Payment Status - Requires VIEW_PRESUPUESTOS permission */}
      {canViewPresupuestos && showUSD && (
        <Tooltip>
          <TooltipTrigger asChild>
            <DollarSign
              className={`h-4 w-4 ${usdPayment.isPaid ? 'text-green-600' : 'text-orange-500'}`}
            />
          </TooltipTrigger>
          <TooltipContent>
            <div className="text-xs">
              <div className="font-semibold">
                {usdPayment.isPaid ? 'USD: Cobrado completamente' : 'USD: Cobro pendiente'}
              </div>
              <div className='font-bold'>
                {formatCurrency(usdPayment.totalPaid, 'USD')} / {formatCurrency(usdPayment.totalAmount, 'USD')}
              </div>
            </div>
          </TooltipContent>
        </Tooltip>
      )}
    </div>
  );
};

TramiteBudgetIndicators.displayName = 'TramiteBudgetIndicators';

export default TramiteBudgetIndicators;
